import { DateRange, DurationParts, EmploymentPeriod } from '../types/employment.types';

/**
 * Parse date string to Date object
 * Handles various date formats and defaults to start of month if day is missing
 */
export const parseEmploymentDate = (dateStr: string): Date => {
  if (!dateStr || dateStr === 'Present') {
    return new Date();
  }

  // Handle YYYY-MM-DD format
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return new Date(dateStr);
  }

  // Handle YYYY-MM format (default to first day of month)
  if (dateStr.match(/^\d{4}-\d{2}$/)) {
    return new Date(`${dateStr}-01`);
  }

  // Handle YYYY format (default to January 1st)
  if (dateStr.match(/^\d{4}$/)) {
    return new Date(`${dateStr}-01-01`);
  }

  // Fallback: try to parse as-is
  const parsed = new Date(dateStr);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
};

/**
 * Calculate duration between two dates
 * Returns years, months, and days
 */
export const calculateDuration = (startDate: Date, endDate: Date | null): DurationParts => {
  const end = endDate || new Date(); // Use current date for ongoing positions
  
  if (startDate > end) {
    return { years: 0, months: 0, days: 0 };
  }

  let years = end.getFullYear() - startDate.getFullYear();
  let months = end.getMonth() - startDate.getMonth();
  let days = end.getDate() - startDate.getDate();

  // Adjust for negative days
  if (days < 0) {
    months--;
    const lastDayOfPrevMonth = new Date(end.getFullYear(), end.getMonth(), 0).getDate();
    days += lastDayOfPrevMonth;
  }

  // Adjust for negative months
  if (months < 0) {
    years--;
    months += 12;
  }

  return { years: Math.max(0, years), months: Math.max(0, months), days: Math.max(0, days) };
};

/**
 * Format duration into human-readable string
 * Examples: "2 years 5 months", "1 year", "6 months", "2 weeks"
 */
export const formatDuration = (duration: DurationParts): string => {
  const { years, months, days } = duration;
  const parts: string[] = [];

  if (years > 0) {
    parts.push(`${years} year${years > 1 ? 's' : ''}`);
  }

  if (months > 0) {
    parts.push(`${months} month${months > 1 ? 's' : ''}`);
  }

  // Only show days if no years/months, and convert to weeks if appropriate
  if (parts.length === 0 && days > 0) {
    if (days >= 7) {
      const weeks = Math.floor(days / 7);
      parts.push(`${weeks} week${weeks > 1 ? 's' : ''}`);
    } else {
      parts.push(`${days} day${days > 1 ? 's' : ''}`);
    }
  }

  return parts.length > 0 ? parts.join(' ') : '0 days';
};

/**
 * Calculate duration string between two date strings
 * Main utility function used by components
 */
export const calculateEmploymentDuration = (
  startDate: string,
  endDate: string
): string => {
  const start = parseEmploymentDate(startDate);
  const end = endDate === 'Present' ? null : parseEmploymentDate(endDate);
  const duration = calculateDuration(start, end);
  return formatDuration(duration);
};

/**
 * Format date for display in organization headers
 * Examples: "Jan 2023", "Dec 2022", "Present"
 */
export const formatDisplayDate = (dateStr: string, t?: (key: string) => string): string => {
  if (dateStr === 'Present') {
    return t ? t('operabase.general.FN_PRESENT') : 'Present';
  }

  try {
    const date = parseEmploymentDate(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      year: 'numeric' 
    });
  } catch {
    return dateStr;
  }
};

/**
 * Create employment period with formatted dates and duration
 * Used for organization group headers
 */
export const createEmploymentPeriod = (
  startDate: string,
  endDate: string
): EmploymentPeriod => {
  const duration = calculateEmploymentDuration(startDate, endDate);
  
  return {
    start: startDate,
    end: endDate,
    duration,
  };
};

/**
 * Format period range for display
 * Examples: "Jan 2023 - Present (1 year 2 months)", "Jun 2020 - Dec 2022 (2 years 7 months)"
 */
export const formatPeriodRange = (period: EmploymentPeriod, t?: (key: string) => string): string => {
  // If start date is empty, hide the entire period (same logic as individual cards)
  if (!period.start || period.start.trim() === '') {
    return '';
  }
  
  const startDisplay = formatDisplayDate(period.start, t);
  const endDisplay = formatDisplayDate(period.end, t);
  
  return `${startDisplay} - ${endDisplay} (${period.duration})`;
};

/**
 * Check if two date ranges overlap
 * Used to determine if employment periods should be grouped together
 */
export const doDateRangesOverlap = (
  range1: DateRange,
  range2: DateRange
): boolean => {
  const start1 = range1.start;
  const end1 = range1.end || new Date();
  const start2 = range2.start;
  const end2 = range2.end || new Date();

  return start1 <= end2 && start2 <= end1;
};

/**
 * Check if there's a gap between two employment periods
 * Used to determine chronological separation
 */
export const hasEmploymentGap = (
  earlierEnd: string,
  laterStart: string
): boolean => {
  if (earlierEnd === 'Present') {
    return false; // No gap if earlier position is ongoing
  }

  const end = parseEmploymentDate(earlierEnd);
  const start = parseEmploymentDate(laterStart);
  
  // Consider a gap if there's more than 30 days between end and start
  const diffMs = start.getTime() - end.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  
  return diffDays > 30;
}; 